import { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { Button, Input } from '@teach/ui'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  editor: Editor
  onClose: () => void
}

const normalizeUrl = (raw: string): string => {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (/^mailto:/i.test(trimmed)) return trimmed
  if (/^\//.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

export const LinkPopper = ({ editor, onClose }: Props) => {
  const m = useMessages(messages)
  const existingHref = editor.getAttributes('link').href as string | undefined
  const [url, setUrl] = useState(existingHref ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const apply = () => {
    const normalized = normalizeUrl(url)
    if (!normalized) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run()
    }
    onClose()
  }

  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    onClose()
  }

  return (
    <div className="flex items-center gap-1 bg-popover border border-border rounded-xl shadow-lg p-1">
      <Input
        data-no-typography=""
        ref={inputRef}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            apply()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
          }
        }}
        placeholder="https://example.com"
        className="h-7 w-64 text-sm border-0 focus-visible:ring-0"
      />
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={apply}>
        {m.apply}
      </Button>
      {existingHref ? (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-destructive hover:text-destructive"
          onClick={remove}
        >
          {m.remove}
        </Button>
      ) : null}
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onClose}>
        {m.cancel}
      </Button>
    </div>
  )
}
